// components/AtelierUnlockPopup.jsx
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, Lock, Sparkles, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const C = {
  bg:       '#020617',
  border:   'rgba(255,255,255,0.06)',
  borderCy: 'rgba(34,211,238,0.25)',
  text:     '#f1f5f9',
  textSub:  '#94a3b8',
  textFaint:'#475569',
  cyan:     '#22d3ee',
  amber:    '#f59e0b',
  emerald:  '#10b981',
};

const Corners = ({ color = C.cyan, size = 7 }) => (
  <>
    <span style={{ position:'absolute', top:-1, left:-1, width:size, height:size, borderTop:`1.5px solid ${color}`, borderLeft:`1.5px solid ${color}` }} />
    <span style={{ position:'absolute', bottom:-1, right:-1, width:size, height:size, borderBottom:`1.5px solid ${color}`, borderRight:`1.5px solid ${color}` }} />
  </>
);

export const AtelierUnlockPopup = ({ ateliers, onClose }) => {
  const navigate = useNavigate();
  if (!ateliers?.length) return null;

  const first = ateliers[0];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(2,6,23,0.85)',
          backdropFilter: 'blur(12px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px',
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ type: 'spring', stiffness: 380, damping: 28 }}
          onClick={e => e.stopPropagation()}
          style={{
            position: 'relative', width: '100%', maxWidth: 480,
            background: 'rgba(8,14,30,0.98)',
            border: `1px solid rgba(34,211,238,0.2)`,
            overflow: 'hidden',
            boxShadow: '0 40px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(34,211,238,0.08)',
          }}
        >
          <Corners color={C.cyan} size={10} />

          {/* Ambient glow */}
          <div style={{
            position: 'absolute', top: -80, left: '50%', transform: 'translateX(-50%)',
            width: 300, height: 300,
            background: 'radial-gradient(circle, rgba(34,211,238,0.08) 0%, transparent 65%)',
            pointerEvents: 'none',
          }} />

          {/* Scanline animée */}
          <motion.div
            animate={{ top: ['-2%', '102%'] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
            style={{
              position: 'absolute', left: 0, right: 0, height: 1, zIndex: 10,
              background: 'rgba(34,211,238,0.4)',
              boxShadow: '0 0 10px rgba(34,211,238,0.6)',
              pointerEvents: 'none',
            }}
          />

          {/* Close */}
          <button onClick={onClose} style={{
            position: 'absolute', top: 14, right: 14, zIndex: 20,
            background: 'none', border: 'none', cursor: 'pointer',
            color: C.textFaint, transition: 'color 0.15s',
          }}
            onMouseEnter={e => e.currentTarget.style.color = C.text}
            onMouseLeave={e => e.currentTarget.style.color = C.textFaint}>
            <X size={14} />
          </button>

          <div style={{ padding: '32px 32px 28px', position: 'relative', zIndex: 2 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
              <motion.div
                animate={{ scale: [1, 1.15, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                  width: 38, height: 38,
                  border: `1px solid rgba(34,211,238,0.3)`,
                  background: 'rgba(34,211,238,0.06)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: C.cyan, position: 'relative',
                }}>
                <Corners color={C.cyan} size={5} />
                <Sparkles size={16} />
              </motion.div>
              <div>
                <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 9, letterSpacing: '0.2em', color: 'rgba(34,211,238,0.5)', textTransform: 'uppercase', margin: 0 }}>
                  Nouvelle_Ressource_Débloquée
                </p>
                <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 8, color: C.textFaint, margin: '2px 0 0' }}>
                  {ateliers.length} atelier{ateliers.length > 1 ? 's' : ''} disponible{ateliers.length > 1 ? 's' : ''}
                </p>
              </div>
            </div>

            {/* Ateliers list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
              {ateliers.map((atelier, i) => (
                <motion.div
                  key={atelier.id}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '14px 16px',
                    background: `${atelier.accentColor}06`,
                    border: `1px solid ${atelier.accentColor}20`,
                    position: 'relative',
                  }}>
                  <Corners color={atelier.accentColor} size={5} />
                  <div style={{ fontSize: 24, flexShrink: 0 }}>{atelier.icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 14, fontWeight: 700, color: C.text, margin: 0,
                    }}>{atelier.title}</p>
                    <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 10, color: C.textFaint, margin: '3px 0 0' }}>
                      {atelier.subtitle} · {atelier.duration} min
                    </p>
                  </div>
                  <div style={{
                    padding: '2px 8px',
                    background: `${atelier.accentColor}10`,
                    border: `1px solid ${atelier.accentColor}25`,
                  }}>
                    <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 9, color: atelier.accentColor, margin: 0, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                      {atelier.level}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Lock → unlock visual */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
              <Lock size={11} color={C.textFaint} />
              <div style={{ flex: 1, height: 1, background: `linear-gradient(to right, rgba(255,255,255,0.06), ${C.emerald}40, rgba(255,255,255,0.06))` }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <motion.div
                  animate={{ scale: [1, 1.2, 1], opacity: [0.6, 1, 0.6] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  style={{ width: 6, height: 6, borderRadius: '50%', background: C.emerald, boxShadow: `0 0 8px ${C.emerald}` }}
                />
                <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 9, color: C.emerald, margin: 0, textTransform: 'uppercase', letterSpacing: '0.15em' }}>
                  Débloqué
                </p>
              </div>
            </motion.div>

            {/* CTA */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => { onClose(); navigate('/ateliers'); }}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  padding: '12px 20px',
                  background: 'linear-gradient(135deg, rgba(34,211,238,0.15), rgba(34,211,238,0.08))',
                  border: `1px solid rgba(34,211,238,0.3)`,
                  color: C.cyan, cursor: 'pointer',
                  fontFamily: "'Roboto Mono', monospace",
                  fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase',
                  transition: 'all 0.2s', position: 'relative',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(34,211,238,0.12)'; e.currentTarget.style.borderColor = 'rgba(34,211,238,0.5)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(34,211,238,0.15), rgba(34,211,238,0.08))'; e.currentTarget.style.borderColor = 'rgba(34,211,238,0.3)'; }}>
                Accéder aux ateliers
                <ArrowRight size={13} />
              </button>
              <button
                onClick={onClose}
                style={{
                  padding: '12px 16px',
                  background: 'none',
                  border: `1px solid ${C.border}`,
                  color: C.textFaint, cursor: 'pointer',
                  fontFamily: "'Roboto Mono', monospace",
                  fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = C.textSub; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textFaint; }}>
                Plus tard
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AtelierUnlockPopup;